/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {Section, SubSection} from './utils';

export function DesignSystemSection() {
  return (
    <Section title='Mixpanel Design System'>
      <SubSection title='Panel Components'>
        <p sel='.desc'>
          This design system website and component library are built using our open source library
          {` `}
          <a attrs={{href: `https://github.com/mixpanel/panel`, target: `_blank`}}>Panel</a>. Panel is a thin layer on
          top of
          {` `}
          <a attrs={{href: `https://developer.mozilla.org/en-US/docs/Web/Web_Components`, target: `_blank`}}>
            Web Components
          </a>
          {` `}
          with React-like virtual DOM rendering.
        </p>
      </SubSection>

      <SubSection title='Design Principles'>
        <h3>Goal</h3>
        <p sel='.desc'>
          Create a cohesive experience for our users. Enable teams to be more efficient, have a shared language across
          the company, and focus deeper on solving user problems. This is a constantly evolving project that seeks to
          improve composability and usability of Mixpanel's user interface.
        </p>

        <div sel='.divider-line' />

        <h3>Product Design Principles</h3>
        <p sel='.desc'>What we strive to do.</p>

        <div sel='.divider-line' />

        <h4>Make hard things easy.</h4>
        <p sel='.desc'>
          Use clear, familiar, and approachable patterns and enable people to unleash the full power of their data.
        </p>

        <h4>Keep it clear.</h4>
        <p sel='.desc'>
          Your data should be easy to parse, easy to understand, and easy to analyze. Regardless of the amount of
          abilities and functionality we provide, if it isn’t clear—it isn’t good.
        </p>

        <h4>Experiment to create value.</h4>
        <p sel='.desc'>
          By making a better product ourselves, Mixpanel empowers customers to make better products. We may end up using
          the very products our customers create.
        </p>

        <div sel='.divider-line' />

        <h3>Design Decision Methods</h3>
        <p sel='.desc'>How we make product design decisions at Mixpanel.</p>

        <h4>Define design requirements.</h4>
        <p sel='.desc'>
          Clearly state the problem and conditions to define design requirements with colleagues and stakeholders. Use
          customer feedback and user stories when they’re available. Answer what’s most important first, then secondary
          and edge cases after that.
        </p>

        <h4>Support decisions with evidence.</h4>
        <p sel='.desc'>
          Create objective measurements when possible. Use customer feedback, usability studies, and usage analytics to
          inform your design decisions. Don’t assume best practices are true in your scenario. Find out for yourself.
        </p>

        <h4>Evaluate the results of your designs.</h4>
        <p sel='.desc'>
          If we’re solving a problem, we should be able to evaluate whether or not our solution is actually solving that
          problem. We don’t need metrics and usability studies to design a t-shirt, but we should have them to analyze
          the customers ability to do something.
        </p>

        <h4>Support decisions with evidence.</h4>
        <p sel='.desc'>
          Create objective measurements when possible. Use customer feedback, usability studies, and usage analytics to
          inform your design decisions. Don’t assume best practices are true in your scenario. Find out for yourself.
        </p>

        <h4>Define design reality and communicate it.</h4>
        <p sel='.desc'>
          Be transparent about the design process and the cause and effect of change requests. Factor in time to
          research, explore, and deliver good work. You can have it fast, cheap, or good, but not all three.
        </p>

        <h4>Support your work with clear reasoning.</h4>
        <p sel='.desc'>
          Good design stands the test of time through well supported rationale. Use existing patterns that work, or when
          necessary build new ones that move us forward and better support the product.
        </p>

        <h4>Improve design quality at every pass.</h4>
        <p sel='.desc'>
          Collaborate and communicate with engineers, project managers, and other designers to create accurate designs
          while also maintaining the flexibility to ship designs. Keep track of inaccuracies and the implications of
          compromises moving forward.
        </p>

        <h4>Explore and experiment.</h4>
        <p sel='.desc'>
          There’s still room to better understand information. Explore and experiment to discover new ways of looking at
          data. We may find better and more valuable solutions by not resting on our laurels or assuming we have answers
          with existing solution by default immediately.
        </p>

        <h4>Checks and balances.</h4>
        <p sel='.desc'>
          Always get a thumbs up from another designer before you press the go button. It might cause things to come to
          screeching holt (so try to do it earlier than later), but your quality will be higher and you will have less
          of a chance to create a one off pattern.
        </p>

        <h4>Document as you go.</h4>
        <p sel='.desc'>
          Using a resource like paper to document your weekly work is an extra step, but will give you support for your
          future ideas. It also gives you an artifact to show your development team.
        </p>

        <h4>Own your copy.</h4>
        <p sel='.desc'>
          We are the only line of defense for bad copy. From feature naming to microcopy. It’s our job to speak the
          language of our customers—empathy.
        </p>

        <div sel='.divider-line' />

        <h3>Design Decision Methods</h3>
        <p sel='.desc'>What we’re doing to realize our values.</p>

        <h4>Keep it simple.</h4>
        <p sel='.desc'>
          “Most systems work best if they are kept simple rather than made complicated; therefore, simplicity should be
          a key goal in design, and unnecessary complexity should be avoided.”
        </p>
        <p sel='.desc'>
          Keep the system as approachable as possible. Document your decisions, your intent, and the ways to use the
          things you create. If you can’t explain it to a stranger, whatever you’re doing is probably too complicated.
          Make obvious decisions whenever possible in order to help fellow teammates understand your logic without
          having to track your history.
        </p>

        <h4>Measure twice, cut once.</h4>
        <p sel='.desc'>
          “One should double-check one’s measurements for accuracy before cutting a piece of wood; otherwise it may be
          necessary to cut again, wasting time and material.”
        </p>
        <p sel='.desc'>
          Make sure the objects you’re creating are properly created. If you’re not going to build it right, why build
          it at all, and all those other proverbs with the same general premise.
        </p>

        <h4>Reduce, reuse, recycle.</h4>
        <p sel='.desc'>
          “The three R’s all help to cut down on the amount of waste we throw away. They conserve natural resources,
          landfill space and energy.”
        </p>
        <p sel='.desc'>
          This library exists to reduce the amount of work we all have to do. We’re saving designers, developers, and
          this entire team a significant amount of time and energy by creating a sustainable design system. We’re
          reducing the amount of times we need to rebuild any specific asset, while making the assets we do use flexible
          enough to reuse, and recycle with the dynamism they’re created with. Use existing styles whenever possible.
        </p>
      </SubSection>
    </Section>
  );
}
